import React, { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { Field } from 'formik';
import ErrorMessage from '@formElements/ErrorMessage';
import cn from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  keyboardOpen,
  keyboardClose,
  keyboardDetails,
  keyboardOpenForClickText,
  keyboardClearField,
  keyboardInput,
} from '@reducers/keyboard';
import { flowType } from '@utility/userInfo';
import style from './keyboardinput.module.scss';
import PhoneNumberComponent from '@components/common/PhoneNumberComponent/indexRevamp';
import { ReactComponent as Cross } from '@assets/images/cross.svg';
import { getObjectValue } from '@utility/utills';

const CustomInputComponent = ({
  field,
  type,
  placeholder,
  onChange,
  customClass,
  keyboardInputValue,
  form,
  maxLength,
  keyboardOpenFn,
  parentCustomClass,
  isPhoneNumber,
  countryCode,
  handleCountryCodeChange,
  ShowLandscapeMode,
  serverAppTheme,
  screenType,
  isLandScape,
  isCounter,
  emailIsCounter,
  keyboardClearField,
  emailFlag,
  setZipcodeKeyOpen,
  storeInfo,
  keyboardInput,
  emailDomain,
  zipcodeKeyOpen,
  isDriveThru
}) => {


  const isKiosk = useMemo(() => flowType() === 'kiosk', []);

  useEffect(() => {
    form.setFieldValue(field.name, keyboardInputValue);
    if (onChange) {
      onChange(keyboardInputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardInputValue]);

  const handleDomainSelect = domain => {
    const atIndex = field.value.lastIndexOf('@');
    if (atIndex !== -1) {
      form.setFieldValue(field.name, field.value.slice(0, atIndex) + domain);
      keyboardInput(field.value.slice(0, atIndex) + domain);
    } else {
      form.setFieldValue(field.name, field.value + domain);
      keyboardInput(field.value + domain);
    }
  };

  const storeData = useMemo(
    () =>
      getObjectValue(
        () => JSON.parse(window.sessionStorage.getItem('auth')),
        {}
      ),
    []
  );

  const hasEmailZipcode = storeData?.loyaltyInfo?.emailOptInFields?.includes(
    'zipcode'
  );
  const hasDisclaimer = storeData?.loyaltyInfo?.supportsOptIn;
  const hasEmail = storeData.loyaltyInfo.optInMethods?.includes('email');

  const calculateBottomPosition = () => {
    const hasDisclaimerContent =
      hasDisclaimer && hasEmail && storeData?.loyaltyInfo?.emailOptInDisclaimer;

    const positionMap = {
      '27': {
        withDisclaimerAndZipcode: '-551px',
        withDisclaimer: '-548px',
        default: `${isLandScape ? '-365px' : '-527px'}`,
      },
      '22': {
        withDisclaimerAndZipcode: '-633px',
        withDisclaimer: `${isLandScape ? '-373px':'-575px'}`,
        default: `${isLandScape ? '-364px':'-528px'}`,
      },
      fallback: `${isLandScape ? '-365px':'-468px'}`,
    };

    const config = positionMap[screenType] || {};

    if (hasDisclaimerContent && hasEmailZipcode)
      return config.withDisclaimerAndZipcode;
    if (hasDisclaimerContent) return config.withDisclaimer;

    return config.default || positionMap.fallback;
  };

  const handleFocus = useCallback(e => {
    e.preventDefault();
    e.target.blur();
  }, []);

  const handleClear = useCallback(() => {
    keyboardClearField(true);
    form.setFieldValue(field.name, '');
    keyboardInput('');
  }, [keyboardClearField, form, field.name, keyboardInput]);

  return (
    <div
      className={cn(
        'col-12 fs-20 large-input row justify-content-center',
        parentCustomClass || 'p-2',
        !isLandScape && 'mx-0'
      )}
    >
      {isPhoneNumber && (
        <PhoneNumberComponent
          countryCode={countryCode}
          styles={{
            top: '50%',
            left: '0',
            transform: 'translateY(-50%)',
            padding: 'p-2',
          }}
          handleCountryCodeChange={handleCountryCodeChange}
          maxMenuHeight={['22', '27'].includes(screenType) ? 200 : 90}
          ShowLandscapeMode={ShowLandscapeMode}
          isPhoneNumber={isPhoneNumber}
          serverAppTheme={serverAppTheme}
          screenType={screenType}
          isLandScape={isLandScape}
          isCounter={isCounter}
          isDriveThru={isDriveThru}
        />
      )}
      <input
        {...field}
        name={field.name}
        value={field.value}
        className={cn(
          customClass,
          emailFlag ? 'col-12 border-0 fs_24' : 'form-control',
          'text-left rounded-0',
          style.inputField, style.textFont
        )}
        type={type}
        maxLength={maxLength}
        style={{
          height: isKiosk ? '5vh' : emailIsCounter ? '' : '3.5rem',
          border: '0',
          borderBottom: serverAppTheme.inputBorder_Bottom,
          background: serverAppTheme.inputBg,
          color: serverAppTheme.primary_text,
          '--placeholder-color': serverAppTheme?.input_placeholder,
          fontSize: (isCounter || isDriveThru) ? (isPhoneNumber ? '32px' : '16px') : '',
        }}
        id="input"
        onFocus={handleFocus}
        placeholder={placeholder}
      />

      {field.value && (
        <div
          role="presentation"
          className={cn(
            'position-absolute',
            (isCounter || isDriveThru) &&
            'h-100 d-flex justify-content-center align-items-center'
          )}
          style={{
            right: emailFlag ? '8px' : '18px',
            top: (!isCounter || !isDriveThru) && (emailFlag ? '6px' : '15px'),
          }}
          onClick={handleClear}
        >
          <Cross fill={serverAppTheme?.iconcolor} />
        </div>
      )}

      {/* Email Entry with Common Domain Pills */}
      {emailFlag && !zipcodeKeyOpen && (
        <div
          className={cn(
            'd-flex o-group-menu w-100 align-items-center pt-2 pb-2 position-absolute pl-2', isLandScape && (screenType === '22' || screenType === '27')? style.emailDomainButton2 : 'flex-wrap',
            style.emailDomainButton
          )}
          style={{
            bottom: emailDomain
              ? screenType === '22'
                ? '-529px'
                : screenType === '27'
                  ? '-528px'
                  : '-465px'
              : calculateBottomPosition(),
          }}
        >
          {storeInfo?.emailDomainNames.map(domain => {
            return (
              <button
                key={domain}
                className={cn(
                  style.oUnselectedGroupBg,
                  'd-inline-flex text-nowrap px-3 fs_16 fw-500 rounded-pill py-1 m-1'
                )}
                type="button"
                onClick={e => {
                  e.preventDefault();
                  handleDomainSelect(domain);
                }}
                style={{
                  color: serverAppTheme?.primary_text,
                  border: `1px solid ${serverAppTheme?.modifiers_border}`,
                }}
              >
                {domain}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const KeyboardInput = props => {
  const {
    name,
    value,
    label,
    type,
    errorLabel,
    readOnly,
    validate,
    maxLength,
    ariaLabel,
    ariaLabelBy,
    autoComplete,
    placeholder,
    customClass,
    onChange,
    isOpen,
    isAlpha = false,
    isDecimal = false,
    parentCustomClass,
    isPhoneNumber,
    countryCode,
    handleCountryCodeChange,
    ShowLandscapeMode,
    isDone,
    screenType,
    isLandScape,
    isCounter,
    emailIsCounter,
    keyboardClearField,
    emailFlag,
    setZipcodeKeyOpen,
    storeInfo,
    keyboardInput,
    emailDomain,
    zipcodeKeyOpen,
    isDriveThru
  } = props;

  const isKeyBoardOpen = useRef(false);
  const updateKeyboardDetails = useCallback(() => {
    props.keyboardDetails({
      type,
      value,
      maxLength,
      isAlpha,
      isDecimal,
      countryCode,
      name,
      isDone,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, value, maxLength, isAlpha, isDecimal, countryCode, name, isDone]);

  const handleKeyboardOpenFn = useCallback(
    () => props.keyboardOpenForClickText(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (isOpen && !isKeyBoardOpen.current) {
      isKeyBoardOpen.current = true;
      props.keyboardOpen();
      updateKeyboardDetails();
    }

    return () => props.keyboardClose();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      props.keyboardInput('');
      updateKeyboardDetails();
    }
  }, [countryCode]);
  return (
    <Fragment>
      <Field
        name={name}
        emailIsCounter={emailIsCounter}
        component={CustomInputComponent}
        isLandScape={isLandScape}
        isCounter={isCounter}
        ShowLandscapeMode={ShowLandscapeMode}
        label={label}
        // type={type}
        errorLabel={errorLabel}
        readOnly={readOnly}
        validate={validate}
        maxLength={maxLength}
        ariaLabel={ariaLabel}
        keyboardOpenFn={handleKeyboardOpenFn}
        ariaLabelBy={ariaLabelBy}
        autoComplete={autoComplete}
        placeholder={placeholder}
        customClass={customClass}
        onChange={onChange}
        keyboardInputValue={props.keyboardInputValue}
        parentCustomClass={parentCustomClass}
        isPhoneNumber={isPhoneNumber}
        countryCode={countryCode}
        handleCountryCodeChange={handleCountryCodeChange}
        serverAppTheme={props?.serverAppTheme}
        screenType={screenType}
        keyboardClearField={keyboardClearField}
        emailFlag={emailFlag}
        setZipcodeKeyOpen={setZipcodeKeyOpen}
        storeInfo={storeInfo}
        keyboardInput={keyboardInput}
        emailDomain={emailDomain}
        zipcodeKeyOpen={zipcodeKeyOpen}
        isDriveThru={isDriveThru}
      />
      {(!isCounter || !isDriveThru) && !isLandScape && (
        <ErrorMessage
          isCounter={isCounter}
          isLandScape={isLandScape}
          emailIsCounter={emailIsCounter}
          name={name}
          isDriveThru={isDriveThru}
        />
      )}
    </Fragment>
  );
};

const mapSateToProps = ({ keyboard, theme }) => ({
  keyboardInputValue: keyboard.keyboardInputValue,
  serverAppTheme: theme.serverAppTheme,
});
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      keyboardOpen,
      keyboardClose,
      keyboardDetails,
      keyboardOpenForClickText,
      keyboardClearField,
      keyboardInput,
    },
    dispatch
  );
const KeyboardInputMemo = React.memo(KeyboardInput, (oldProps, newProps) => {
  return (
    oldProps.value === newProps.value &&
    oldProps.isOpen === newProps.isOpen &&
    oldProps.isLandScape === newProps.isLandScape &&
    oldProps.keyboardInputValue === newProps.keyboardInputValue &&
    oldProps.countryCode === newProps.countryCode
  );
});
export default connect(mapSateToProps, mapDispatchToProps)(KeyboardInputMemo);
